import { useState, useEffect } from 'react';
import { palette, transition } from '../../styles/tokens';
import { Card } from '../common/Card';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/authStore';
import type { NotificationStats } from '../../hooks/useNotificationStats';

interface Props {
  stats: NotificationStats;
  loading: boolean;
}

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  shift_reminder: 'Shift Reminder', shift_swap_request: 'Shift Swap Request',
  shift_swap_approved: 'Swap Approved', shift_swap_declined: 'Swap Declined',
  schedule_published: 'Schedule Published', leave_request: 'Leave Request',
  leave_approved: 'Leave Approved', leave_declined: 'Leave Declined',
  clock_reminder: 'Clock Reminder', ot_alert: 'OT Alert',
  new_message: 'New Message', invoice_sent: 'Invoice Sent',
  payment_received: 'Payment Received', booking_confirmed: 'Booking Confirmed',
  booking_cancelled: 'Booking Cancelled', daily_summary: 'Daily Summary',
};

function Toggle({ on, onChange }: { on: boolean; onChange: () => void }) {
  return (
    <button onClick={onChange} style={{
      width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
      background: on ? palette.accent : palette.g200, position: 'relative',
      transition: `background ${transition.fast}`, flexShrink: 0,
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: 9, background: '#fff',
        position: 'absolute', top: 2, left: on ? 20 : 2,
        transition: `left ${transition.fast}`, boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
      }} />
    </button>
  );
}

export function NotificationSection({ stats, loading }: Props) {
  const business = useAuthStore(s => s.business);
  const [afterHoursReply, setAfterHoursReply] = useState(false);
  const [autoQuoteFollowup, setAutoQuoteFollowup] = useState(true);

  useEffect(() => {
    if (!business?.id) return;
    supabase.from('businesses').select('auto_after_hours_reply, auto_quote_followup')
      .eq('id', business.id).maybeSingle()
      .then(({ data }) => {
        if (data) {
          setAfterHoursReply(data.auto_after_hours_reply ?? false);
          setAutoQuoteFollowup(data.auto_quote_followup !== false);
        }
      });
  }, [business?.id]);

  const toggleAfterHours = async () => {
    if (!business?.id) return;
    const next = !afterHoursReply;
    setAfterHoursReply(next);
    await supabase.from('businesses').update({ auto_after_hours_reply: next }).eq('id', business.id);
  };

  const toggleQuoteFollowup = async () => {
    if (!business?.id) return;
    const next = !autoQuoteFollowup;
    setAutoQuoteFollowup(next);
    await supabase.from('businesses').update({ auto_quote_followup: next }).eq('id', business.id);
  };

  if (loading) return null;

  return (
    <>
      <Card title="Notifications">
        {/* Stats row */}
        <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: palette.g800 }}>{stats.sentThisWeek}</div>
            <div style={{ fontSize: 11, color: palette.g400 }}>Sent this week</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: palette.amber }}>{stats.pending}</div>
            <div style={{ fontSize: 11, color: palette.g400 }}>Pending</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: stats.failed > 0 ? palette.red : palette.g400 }}>{stats.failed}</div>
            <div style={{ fontSize: 11, color: palette.g400 }}>Failed</div>
          </div>
        </div>

        {/* Active types */}
        {stats.activeTypes.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: palette.g500, marginBottom: 8 }}>Active Notification Types</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {stats.activeTypes.map(t => (
                <span key={t} style={{ padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: palette.accentWash, color: palette.accentDeep }}>
                  {NOTIFICATION_TYPE_LABELS[t] ?? t.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ marginTop: 14, fontSize: 11, color: palette.g400, fontStyle: 'italic' }}>
          Push notification preferences are managed from the mobile app.
        </div>
      </Card>

      <Card title="Automation">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: `1px solid ${palette.g100}` }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: palette.g800 }}>After-hours auto-reply</div>
            <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>Auto-reply to client messages outside business hours (max 1 per conversation every 12h)</div>
          </div>
          <Toggle on={afterHoursReply} onChange={toggleAfterHours} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: palette.g800 }}>Quote follow-up emails</div>
            <div style={{ fontSize: 12, color: palette.g400, marginTop: 2 }}>Automatically email clients 3 days after an unanswered quote</div>
          </div>
          <Toggle on={autoQuoteFollowup} onChange={toggleQuoteFollowup} />
        </div>
      </Card>
    </>
  );
}
